import { 
  Stethoscope, ShoppingBag, Building2, Home, Scale, Utensils, Car, 
  Plane, GraduationCap, Dumbbell, Scissors, Camera, Wrench, Truck,
  CreditCard, Shield, Heart, Baby, Dog, Flower, Music, Gamepad2,
  Palette, BookOpen, Briefcase, Factory, HardHat, Wifi, Zap, Users,
  Droplets, TreePine, Mountain, Ship, Train, Cog, Microscope,
  Pill, Glasses, Sparkles, Coffee, Wine, Pizza, ShoppingCart,
  Package, Warehouse, Printer, Monitor, Headphones, Radio
} from "lucide-react";
import React from "react";

export interface Industry {
  id: string;
  name: string;
  icon: string;
  tagline: string;
  description: string;
  useCases: string[];
  benefits: string[];
  callTypes: ("inbound" | "outbound" | "both")[];
  integrations: string[];
  stats: { label: string; value: string }[];
  gradient: string;
  category: string;
  image?: string;
}

// Industry images mapping
export const industryImages: Record<string, string> = {
  "medical-clinics": "/industries/medical-clinics.jpg",
  "real-estate-agencies": "/industries/real-estate.jpg",
  "restaurants-dining": "/industries/restaurants.jpg",
  "auto-dealerships": "/industries/auto-dealerships.jpg",
  "law-firms": "/industries/law-firms.jpg",
  "hotels-resorts": "/industries/hotels.jpg",
  "ecommerce-stores": "/industries/ecommerce.jpg",
  "hvac-services": "/industries/hvac-services.jpg",
  "dental-practices": "/industries/dental-practices.jpg",
  "insurance-agencies": "/industries/insurance-agencies.jpg",
  "salons-barbershops": "/industries/salons-barbershops.jpg",
  "plumbing-services": "/industries/plumbing-services.jpg",
  "fitness-studios": "/industries/fitness-studios.jpg",
  "saas-companies": "/industries/saas-companies.jpg",
  "veterinary-clinics": "/industries/veterinary-clinics.jpg",
};

export const industries: Industry[] = [
  // Healthcare & Medical
  {
    id: "medical-clinics",
    name: "Medical Clinics",
    icon: "Stethoscope",
    tagline: "24/7 Patient Care Without the Wait",
    description: "Transform your medical practice with AI phone agents that handle appointment scheduling, prescription refill requests, and patient inquiries around the clock while maintaining HIPAA compliance.",
    useCases: ["Appointment scheduling and reminders", "Prescription refill requests", "Lab results notifications", "Insurance verification", "After-hours triage"],
    benefits: ["Reduce no-shows by up to 67%", "Handle 300+ calls daily", "HIPAA compliant conversations", "Seamless EHR integration"],
    callTypes: ["inbound", "both"],
    integrations: ["Epic", "Cerner", "Athenahealth", "Google Calendar"],
    stats: [{ label: "No-Show Reduction", value: "67%" }, { label: "Patient Satisfaction", value: "4.9/5" }],
    gradient: "from-emerald-500/20 to-teal-500/20",
    category: "Healthcare"
  },
  {
    id: "dental-practices",
    name: "Dental Practices",
    icon: "Sparkles",
    tagline: "Smile-Worthy Patient Communication",
    description: "Automate appointment booking, treatment reminders, and insurance inquiries for your dental practice. Our AI handles patient calls with care while your team focuses on creating beautiful smiles.",
    useCases: ["Appointment scheduling", "Treatment plan follow-ups", "Insurance claim status", "Emergency dental inquiries", "Recall reminders"],
    benefits: ["Fill last-minute cancellations automatically", "Reduce front desk workload by 50%", "Increase patient retention", "24/7 emergency routing"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Dentrix", "Eaglesoft", "Open Dental", "Patterson"],
    stats: [{ label: "Appointment Fill Rate", value: "89%" }, { label: "Time Saved", value: "25 hrs/week" }],
    gradient: "from-sky-500/20 to-blue-500/20",
    category: "Healthcare"
  },
  {
    id: "veterinary-clinics",
    name: "Veterinary Clinics",
    icon: "Dog",
    tagline: "Never Miss a Pet Parent's Call",
    description: "Provide exceptional care to pet parents with AI agents that schedule appointments, handle medication refills, and answer common pet care questions 24/7.",
    useCases: ["Appointment booking", "Vaccination reminders", "Prescription refills", "Post-surgery follow-ups", "Emergency triage"],
    benefits: ["Handle after-hours pet emergencies", "Reduce callback burden", "Improve pet parent satisfaction", "Streamline medication refills"],
    callTypes: ["inbound", "both"],
    integrations: ["Avimark", "Cornerstone", "VetBadger", "eVetPractice"],
    stats: [{ label: "Call Answer Rate", value: "100%" }, { label: "Emergency Response", value: "<30 sec" }],
    gradient: "from-amber-500/20 to-yellow-500/20",
    category: "Healthcare"
  },
  {
    id: "pharmacies",
    name: "Pharmacies",
    icon: "Pill",
    tagline: "Prescription Management Made Easy",
    description: "Streamline prescription refills, transfer requests, and medication inquiries with AI phone agents that integrate directly with your pharmacy management system.",
    useCases: ["Prescription refill requests", "Transfer processing", "Drug interaction inquiries", "Insurance verification", "Pickup notifications"],
    benefits: ["Process refills 24/7", "Reduce hold times to zero", "Improve medication adherence", "Free pharmacists for consultations"],
    callTypes: ["inbound", "outbound"],
    integrations: ["McKesson", "Cardinal Health", "PioneerRx", "Liberty Software"],
    stats: [{ label: "Refill Processing", value: "2 min avg" }, { label: "Customer Satisfaction", value: "4.8/5" }],
    gradient: "from-red-500/20 to-rose-500/20",
    category: "Healthcare"
  },
  {
    id: "mental-health",
    name: "Mental Health Practices",
    icon: "Heart",
    tagline: "Compassionate Care, Always Available",
    description: "Support patients with sensitive, HIPAA-compliant AI phone agents that handle appointment scheduling, insurance verification, and crisis resource referrals.",
    useCases: ["Session scheduling", "Insurance verification", "Crisis resource referral", "Intake questionnaires", "Appointment reminders"],
    benefits: ["Reduce barriers to care", "Handle sensitive calls appropriately", "24/7 crisis resource access", "Maintain patient privacy"],
    callTypes: ["inbound"],
    integrations: ["SimplePractice", "TherapyNotes", "Jane App", "Headway"],
    stats: [{ label: "Appointment Booking", value: "+45%" }, { label: "Patient Reach", value: "24/7" }],
    gradient: "from-pink-500/20 to-purple-500/20",
    category: "Healthcare"
  },
  {
    id: "optometry",
    name: "Optometry & Eye Care",
    icon: "Glasses",
    tagline: "Clear Vision for Your Practice",
    description: "Manage eye exam appointments, frame orders, and contact lens refills with AI agents that see your practice's success clearly.",
    useCases: ["Eye exam scheduling", "Contact lens reorders", "Frame availability checks", "Insurance verification", "Prescription pickups"],
    benefits: ["Increase exam bookings by 35%", "Automate contact lens refills", "Reduce no-shows", "Handle insurance questions"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Revolution EHR", "Crystal PM", "Compulink", "Eyefinity"],
    stats: [{ label: "Booking Increase", value: "35%" }, { label: "Refill Automation", value: "90%" }],
    gradient: "from-indigo-500/20 to-violet-500/20",
    category: "Healthcare"
  },
  
  // Real Estate & Property
  {
    id: "real-estate-agencies",
    name: "Real Estate Agencies",
    icon: "Home",
    tagline: "Qualify Leads While You Sleep",
    description: "Never miss a hot lead again. AI agents qualify buyers, schedule showings, and provide property information 24/7, ensuring your agents focus on closing deals.",
    useCases: ["Lead qualification", "Showing scheduling", "Property inquiries", "Open house reminders", "Follow-up calls"],
    benefits: ["3x more qualified leads", "24/7 lead capture", "Automated showing scheduling", "CRM integration"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Salesforce", "Follow Up Boss", "BoomTown", "Zillow"],
    stats: [{ label: "Lead Qualification", value: "3x" }, { label: "Agent Time Saved", value: "25 hrs/wk" }],
    gradient: "from-blue-500/20 to-cyan-500/20",
    category: "Real Estate"
  },
  {
    id: "property-management",
    name: "Property Management",
    icon: "Building2",
    tagline: "Happy Tenants, Happy Owners",
    description: "Handle tenant inquiries, maintenance requests, and lease renewals automatically. Your AI agent becomes the first point of contact for all property-related calls.",
    useCases: ["Maintenance request intake", "Rent payment reminders", "Lease renewal calls", "Property inquiries", "Emergency dispatch"],
    benefits: ["24/7 maintenance intake", "Reduce tenant complaints", "Automate rent reminders", "Faster emergency response"],
    callTypes: ["inbound", "outbound"],
    integrations: ["AppFolio", "Buildium", "Yardi", "RentManager"],
    stats: [{ label: "Response Time", value: "<1 min" }, { label: "Tenant Satisfaction", value: "4.7/5" }],
    gradient: "from-slate-500/20 to-gray-500/20",
    category: "Real Estate"
  },
  {
    id: "mortgage-lending",
    name: "Mortgage & Lending",
    icon: "CreditCard",
    tagline: "Pre-Qualify Borrowers Instantly",
    description: "Accelerate your lending pipeline with AI agents that pre-qualify borrowers, collect documentation, and keep applicants informed throughout the process.",
    useCases: ["Pre-qualification calls", "Document collection reminders", "Rate inquiries", "Application status updates", "Refinance outreach"],
    benefits: ["Pre-qualify leads 24/7", "Reduce application abandonment", "Automate status updates", "Increase conversion rates"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Encompass", "Blend", "Floify", "Salesforce"],
    stats: [{ label: "Pre-Qual Speed", value: "5 min" }, { label: "Conversion Increase", value: "28%" }],
    gradient: "from-green-500/20 to-emerald-500/20",
    category: "Real Estate"
  },
  
  // Hospitality & Travel
  {
    id: "hotels-resorts",
    name: "Hotels & Resorts",
    icon: "Building2",
    tagline: "5-Star Service, 24/7 Availability",
    description: "Deliver exceptional guest experiences with AI concierge services handling reservations, room service, and guest requests around the clock.",
    useCases: ["Reservation booking", "Room service orders", "Concierge requests", "Wake-up calls", "Check-in/out assistance"],
    benefits: ["Increase direct bookings by 38%", "Reduce OTA commissions", "24/7 guest support", "Multi-language service"],
    callTypes: ["inbound"],
    integrations: ["Opera PMS", "Cloudbeds", "Mews", "Salesforce"],
    stats: [{ label: "Direct Bookings", value: "+38%" }, { label: "Guest Satisfaction", value: "94%" }],
    gradient: "from-amber-500/20 to-orange-500/20",
    category: "Hospitality"
  },
  {
    id: "restaurants-dining",
    name: "Restaurants & Dining",
    icon: "Utensils",
    tagline: "Never Miss a Reservation Again",
    description: "Fill tables and manage takeout orders with AI agents that handle reservations, confirm bookings, and process phone orders seamlessly.",
    useCases: ["Reservation booking", "Order taking", "Waitlist management", "Special event inquiries", "Catering requests"],
    benefits: ["Fill more tables", "Reduce no-shows by 50%", "Handle peak call volume", "Upsell specials automatically"],
    callTypes: ["inbound"],
    integrations: ["OpenTable", "Resy", "Toast", "Square"],
    stats: [{ label: "No-Show Reduction", value: "50%" }, { label: "Revenue Increase", value: "23%" }],
    gradient: "from-orange-500/20 to-red-500/20",
    category: "Hospitality"
  },
  {
    id: "travel-agencies",
    name: "Travel Agencies",
    icon: "Plane",
    tagline: "Book Dream Vacations Around the Clock",
    description: "Help travelers plan their perfect trips with AI agents that handle inquiries, booking confirmations, and itinerary changes 24/7.",
    useCases: ["Trip inquiries", "Booking confirmations", "Itinerary changes", "Travel insurance offers", "Last-minute deals"],
    benefits: ["Capture leads 24/7", "Handle high volume inquiries", "Upsell travel packages", "Reduce booking abandonment"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Sabre", "Amadeus", "Travelport", "Salesforce"],
    stats: [{ label: "Booking Increase", value: "32%" }, { label: "Lead Capture", value: "24/7" }],
    gradient: "from-sky-500/20 to-indigo-500/20",
    category: "Hospitality"
  },
  {
    id: "event-venues",
    name: "Event Venues",
    icon: "Music",
    tagline: "Book More Events, Less Effort",
    description: "Manage venue inquiries, tour scheduling, and event coordination with AI agents that showcase your space and close bookings.",
    useCases: ["Venue availability checks", "Tour scheduling", "Event inquiries", "Catering coordination", "Contract follow-ups"],
    benefits: ["Respond to inquiries instantly", "Schedule tours automatically", "Capture every lead", "Reduce booking time"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Tripleseat", "Planning Pod", "Honeybook", "Salesforce"],
    stats: [{ label: "Inquiry Response", value: "<1 min" }, { label: "Tour Bookings", value: "+45%" }],
    gradient: "from-purple-500/20 to-pink-500/20",
    category: "Hospitality"
  },
  {
    id: "spas-wellness",
    name: "Spas & Wellness Centers",
    icon: "Sparkles",
    tagline: "Relaxation Starts with the First Call",
    description: "Create seamless booking experiences for your clients with AI agents that schedule appointments, explain services, and handle membership inquiries.",
    useCases: ["Appointment booking", "Service inquiries", "Membership management", "Gift card sales", "Package promotions"],
    benefits: ["Book appointments 24/7", "Increase package sales", "Reduce no-shows", "Upsell treatments"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Mindbody", "Vagaro", "Booker", "WellnessLiving"],
    stats: [{ label: "Booking Rate", value: "+40%" }, { label: "Package Sales", value: "+25%" }],
    gradient: "from-teal-500/20 to-cyan-500/20",
    category: "Hospitality"
  },
  
  // Automotive
  {
    id: "auto-dealerships",
    name: "Auto Dealerships",
    icon: "Car",
    tagline: "Sell More Cars, Answer Every Call",
    description: "Convert more leads into test drives with AI agents that qualify buyers, schedule appointments, and follow up on inquiries automatically.",
    useCases: ["Lead qualification", "Test drive scheduling", "Service appointments", "Trade-in inquiries", "Financing questions"],
    benefits: ["Qualify leads 24/7", "Increase test drives by 40%", "Reduce lead response time", "Automate follow-ups"],
    callTypes: ["inbound", "outbound"],
    integrations: ["DealerSocket", "VinSolutions", "CDK Global", "Salesforce"],
    stats: [{ label: "Test Drives", value: "+40%" }, { label: "Lead Response", value: "<30 sec" }],
    gradient: "from-blue-500/20 to-indigo-500/20",
    category: "Automotive"
  },
  {
    id: "auto-repair",
    name: "Auto Repair Shops",
    icon: "Wrench",
    tagline: "Keep Customers Coming Back",
    description: "Manage service appointments, provide repair updates, and send maintenance reminders with AI agents that keep your bays full.",
    useCases: ["Service scheduling", "Repair status updates", "Estimate approvals", "Maintenance reminders", "Parts availability"],
    benefits: ["Fill appointment slots", "Reduce phone interruptions", "Automate service reminders", "Improve customer retention"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Shop-Ware", "Mitchell 1", "Tekmetric", "RepairShopr"],
    stats: [{ label: "Appointment Fill Rate", value: "95%" }, { label: "Customer Retention", value: "+30%" }],
    gradient: "from-gray-500/20 to-zinc-500/20",
    category: "Automotive"
  },
  {
    id: "car-rental",
    name: "Car Rental Companies",
    icon: "Car",
    tagline: "Rent More, Staff Less",
    description: "Process reservations, handle modifications, and manage pickups with AI agents that drive your rental business forward.",
    useCases: ["Reservation booking", "Modification requests", "Pickup/return coordination", "Insurance inquiries", "Loyalty program"],
    benefits: ["24/7 reservation capability", "Reduce booking abandonment", "Handle peak demand", "Upsell insurance/upgrades"],
    callTypes: ["inbound", "outbound"],
    integrations: ["HQ Rental Software", "Bluebird", "RentWorks", "Salesforce"],
    stats: [{ label: "Booking Rate", value: "+35%" }, { label: "Upsell Rate", value: "28%" }],
    gradient: "from-emerald-500/20 to-green-500/20",
    category: "Automotive"
  },
  
  // Professional Services
  {
    id: "law-firms",
    name: "Law Firms",
    icon: "Scale",
    tagline: "Capture Every Case, Miss No Client",
    description: "Qualify potential clients, schedule consultations, and manage intake with AI agents that understand the urgency of legal matters.",
    useCases: ["Client intake", "Consultation scheduling", "Case status updates", "Document requests", "Billing inquiries"],
    benefits: ["24/7 client intake", "Qualify cases automatically", "Reduce missed calls", "Improve client satisfaction"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Clio", "MyCase", "PracticePanther", "Salesforce"],
    stats: [{ label: "Client Capture", value: "+55%" }, { label: "Intake Speed", value: "3 min" }],
    gradient: "from-amber-600/20 to-yellow-600/20",
    category: "Professional Services"
  },
  {
    id: "accounting-firms",
    name: "Accounting & CPA Firms",
    icon: "Briefcase",
    tagline: "Focus on Numbers, Not Phone Calls",
    description: "Handle tax season call volume, schedule consultations, and manage client inquiries with AI agents that understand financial urgency.",
    useCases: ["Appointment scheduling", "Document collection", "Tax deadline reminders", "Service inquiries", "Billing questions"],
    benefits: ["Handle tax season volume", "Schedule consultations 24/7", "Automate document reminders", "Improve client retention"],
    callTypes: ["inbound", "outbound"],
    integrations: ["QuickBooks", "Xero", "Practice CS", "Canopy"],
    stats: [{ label: "Call Handling", value: "500+/day" }, { label: "Client Satisfaction", value: "4.8/5" }],
    gradient: "from-blue-600/20 to-indigo-600/20",
    category: "Professional Services"
  },
  {
    id: "consulting-firms",
    name: "Consulting Firms",
    icon: "Briefcase",
    tagline: "Convert Inquiries into Engagements",
    description: "Qualify leads, schedule discovery calls, and manage client communications with AI agents that represent your expertise professionally.",
    useCases: ["Lead qualification", "Discovery call scheduling", "Proposal follow-ups", "Project status updates", "Contract renewals"],
    benefits: ["Capture leads 24/7", "Qualify prospects automatically", "Reduce admin overhead", "Focus on billable work"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Salesforce", "HubSpot", "Calendly", "Monday.com"],
    stats: [{ label: "Lead Qualification", value: "2x" }, { label: "Booking Rate", value: "+45%" }],
    gradient: "from-violet-500/20 to-purple-500/20",
    category: "Professional Services"
  },
  {
    id: "financial-advisors",
    name: "Financial Advisors",
    icon: "CreditCard",
    tagline: "Grow Your Practice, Not Your Phone Time",
    description: "Schedule consultations, handle account inquiries, and nurture prospects with AI agents that understand the importance of trust in financial services.",
    useCases: ["Consultation scheduling", "Account inquiries", "Review meeting reminders", "Prospect follow-ups", "Event invitations"],
    benefits: ["Capture HNW leads 24/7", "Schedule reviews automatically", "Improve client communication", "Comply with regulations"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Redtail", "Wealthbox", "Orion", "Salesforce"],
    stats: [{ label: "AUM Growth", value: "+22%" }, { label: "Client Meetings", value: "+35%" }],
    gradient: "from-green-600/20 to-teal-600/20",
    category: "Professional Services"
  },
  {
    id: "insurance-agencies",
    name: "Insurance Agencies",
    icon: "Shield",
    tagline: "Quote Faster, Close More",
    description: "Handle quote requests, policy questions, and claims intake with AI agents that provide instant service and drive policy sales.",
    useCases: ["Quote requests", "Policy inquiries", "Claims intake", "Renewal reminders", "Coverage reviews"],
    benefits: ["Provide quotes 24/7", "Speed up claims intake", "Reduce policy lapses", "Cross-sell coverage"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Applied Epic", "Hawksoft", "AgencyZoom", "Salesforce"],
    stats: [{ label: "Quote Speed", value: "5 min" }, { label: "Retention Rate", value: "+18%" }],
    gradient: "from-blue-500/20 to-sky-500/20",
    category: "Professional Services"
  },
  {
    id: "recruitment-staffing",
    name: "Recruitment & Staffing",
    icon: "Users",
    tagline: "Screen Candidates at Scale",
    description: "Automate candidate screening, interview scheduling, and follow-ups with AI agents that help you place talent faster.",
    useCases: ["Candidate screening", "Interview scheduling", "Job opportunity outreach", "Reference checks", "Onboarding coordination"],
    benefits: ["Screen candidates 24/7", "Reduce time-to-fill", "Improve candidate experience", "Scale without hiring"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Bullhorn", "JobAdder", "Lever", "Greenhouse"],
    stats: [{ label: "Screening Speed", value: "3x faster" }, { label: "Time-to-Fill", value: "-40%" }],
    gradient: "from-orange-500/20 to-amber-500/20",
    category: "Professional Services"
  },
  
  // Home Services
  {
    id: "hvac-services",
    name: "HVAC Services",
    icon: "Zap",
    tagline: "Never Miss an Emergency Call",
    description: "Handle service calls, schedule maintenance, and dispatch technicians with AI agents that keep your business running 24/7.",
    useCases: ["Emergency dispatch", "Service scheduling", "Maintenance reminders", "Estimate requests", "Warranty inquiries"],
    benefits: ["24/7 emergency response", "Reduce missed calls", "Automate seasonal reminders", "Improve first-call resolution"],
    callTypes: ["inbound", "outbound"],
    integrations: ["ServiceTitan", "Housecall Pro", "FieldEdge", "Jobber"],
    stats: [{ label: "Emergency Response", value: "<1 min" }, { label: "Service Calls", value: "+35%" }],
    gradient: "from-sky-500/20 to-blue-500/20",
    category: "Home Services"
  },
  {
    id: "plumbing-services",
    name: "Plumbing Services",
    icon: "Droplets",
    tagline: "Answer Emergencies, Anytime",
    description: "Capture emergency calls, schedule service, and manage customer inquiries with AI agents that understand plumbing urgency.",
    useCases: ["Emergency dispatch", "Service scheduling", "Estimate requests", "Follow-up calls", "Maintenance plans"],
    benefits: ["24/7 emergency intake", "Reduce after-hours costs", "Improve booking rates", "Automate follow-ups"],
    callTypes: ["inbound", "outbound"],
    integrations: ["ServiceTitan", "Housecall Pro", "FieldPulse", "Jobber"],
    stats: [{ label: "Call Capture", value: "100%" }, { label: "Revenue", value: "+28%" }],
    gradient: "from-blue-600/20 to-indigo-600/20",
    category: "Home Services"
  },
  {
    id: "electrical-services",
    name: "Electrical Services",
    icon: "Zap",
    tagline: "Power Up Your Customer Service",
    description: "Handle service requests, emergency calls, and estimate inquiries with AI agents that keep your electricians focused on the work.",
    useCases: ["Service scheduling", "Emergency dispatch", "Estimate requests", "Project follow-ups", "Maintenance reminders"],
    benefits: ["Capture every lead", "24/7 emergency handling", "Reduce admin work", "Improve customer retention"],
    callTypes: ["inbound", "outbound"],
    integrations: ["ServiceTitan", "Housecall Pro", "Workiz", "Jobber"],
    stats: [{ label: "Lead Capture", value: "100%" }, { label: "Booking Rate", value: "+40%" }],
    gradient: "from-yellow-500/20 to-amber-500/20",
    category: "Home Services"
  },
  {
    id: "landscaping",
    name: "Landscaping & Lawn Care",
    icon: "TreePine",
    tagline: "Grow Your Business, Not Your Phone Time",
    description: "Manage seasonal demand, schedule services, and handle estimates with AI agents that help your landscaping business bloom.",
    useCases: ["Service scheduling", "Estimate requests", "Seasonal reminders", "Weather rescheduling", "Payment reminders"],
    benefits: ["Handle seasonal volume", "Automate rescheduling", "Improve customer retention", "Upsell services"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Jobber", "LMN", "Service Autopilot", "Yardbook"],
    stats: [{ label: "Seasonal Capacity", value: "+200%" }, { label: "Customer Retention", value: "+25%" }],
    gradient: "from-green-500/20 to-emerald-500/20",
    category: "Home Services"
  },
  {
    id: "pest-control",
    name: "Pest Control",
    icon: "Shield",
    tagline: "Respond to Every Critter Call",
    description: "Handle urgent pest calls, schedule treatments, and manage recurring services with AI agents that keep your routes full.",
    useCases: ["Emergency dispatch", "Treatment scheduling", "Recurring service management", "Estimate requests", "Follow-up calls"],
    benefits: ["24/7 emergency response", "Optimize technician routes", "Improve recurring revenue", "Reduce cancellations"],
    callTypes: ["inbound", "outbound"],
    integrations: ["PestPac", "ServSuite", "FieldRoutes", "Briostack"],
    stats: [{ label: "Response Time", value: "<2 min" }, { label: "Recurring Revenue", value: "+32%" }],
    gradient: "from-orange-600/20 to-red-600/20",
    category: "Home Services"
  },
  {
    id: "cleaning-services",
    name: "Cleaning Services",
    icon: "Sparkles",
    tagline: "Clean Up Your Booking Process",
    description: "Book appointments, manage recurring services, and handle customer requests with AI agents that keep your cleaning business spotless.",
    useCases: ["Appointment booking", "Estimate requests", "Recurring service management", "Rescheduling", "Quality follow-ups"],
    benefits: ["Book appointments 24/7", "Reduce scheduling conflicts", "Improve customer satisfaction", "Upsell deep cleans"],
    callTypes: ["inbound", "outbound"],
    integrations: ["ZenMaid", "Launch27", "Jobber", "Housecall Pro"],
    stats: [{ label: "Booking Rate", value: "+45%" }, { label: "Customer Retention", value: "89%" }],
    gradient: "from-cyan-500/20 to-teal-500/20",
    category: "Home Services"
  },
  {
    id: "roofing-contractors",
    name: "Roofing Contractors",
    icon: "HardHat",
    tagline: "Capture Storm Leads, Rain or Shine",
    description: "Handle inspection requests, schedule estimates, and manage storm response with AI agents that keep your crews busy.",
    useCases: ["Estimate scheduling", "Storm damage response", "Insurance coordination", "Project updates", "Follow-up calls"],
    benefits: ["Capture storm leads 24/7", "Reduce lead response time", "Improve conversion rates", "Automate follow-ups"],
    callTypes: ["inbound", "outbound"],
    integrations: ["AccuLynx", "JobNimbus", "Roofr", "Salesforce"],
    stats: [{ label: "Lead Capture", value: "100%" }, { label: "Conversion Rate", value: "+38%" }],
    gradient: "from-slate-600/20 to-gray-600/20",
    category: "Home Services"
  },
  {
    id: "moving-companies",
    name: "Moving Companies",
    icon: "Truck",
    tagline: "Move More, Stress Less",
    description: "Handle quote requests, schedule moves, and confirm bookings with AI agents that make moving day easier for everyone.",
    useCases: ["Quote requests", "Move scheduling", "Confirmation calls", "Inventory collection", "Follow-up surveys"],
    benefits: ["Quote requests 24/7", "Reduce booking abandonment", "Confirm moves automatically", "Improve reviews"],
    callTypes: ["inbound", "outbound"],
    integrations: ["MoveitPro", "SmartMoving", "Supermove", "Salesforce"],
    stats: [{ label: "Quote Requests", value: "+50%" }, { label: "Booking Confirmation", value: "95%" }],
    gradient: "from-amber-500/20 to-orange-500/20",
    category: "Home Services"
  },
  
  // Retail & E-Commerce
  {
    id: "ecommerce-stores",
    name: "E-Commerce Stores",
    icon: "ShoppingCart",
    tagline: "Recover Carts, Boost Revenue",
    description: "Recover abandoned carts, handle order inquiries, and drive upsells with AI agents that turn browsers into buyers.",
    useCases: ["Abandoned cart recovery", "Order status updates", "Return/exchange processing", "Product inquiries", "VIP customer outreach"],
    benefits: ["Recover 45% of abandoned carts", "24/7 customer support", "Increase average order value", "Reduce return calls"],
    callTypes: ["outbound", "inbound"],
    integrations: ["Shopify", "WooCommerce", "BigCommerce", "Magento"],
    stats: [{ label: "Cart Recovery", value: "45%" }, { label: "Revenue Increase", value: "$127K/mo" }],
    gradient: "from-purple-500/20 to-pink-500/20",
    category: "Retail"
  },
  {
    id: "retail-stores",
    name: "Retail Stores",
    icon: "ShoppingBag",
    tagline: "Answer Every Customer Call",
    description: "Handle product availability, store hours, and customer inquiries with AI agents that free your staff to serve in-store customers.",
    useCases: ["Product availability", "Store hours/locations", "Order pickup", "Returns inquiries", "Special orders"],
    benefits: ["Reduce in-store interruptions", "Consistent information", "Handle call overflow", "Improve customer service"],
    callTypes: ["inbound"],
    integrations: ["Square", "Lightspeed", "Shopify POS", "Clover"],
    stats: [{ label: "Call Handling", value: "100%" }, { label: "Staff Time Saved", value: "15 hrs/wk" }],
    gradient: "from-rose-500/20 to-pink-500/20",
    category: "Retail"
  },
  {
    id: "furniture-stores",
    name: "Furniture Stores",
    icon: "Home",
    tagline: "Furnish Better Customer Experiences",
    description: "Manage delivery scheduling, product inquiries, and financing questions with AI agents that help close more furniture sales.",
    useCases: ["Delivery scheduling", "Product availability", "Financing inquiries", "Warranty claims", "Design consultations"],
    benefits: ["Schedule deliveries efficiently", "Handle financing questions", "Reduce showroom interruptions", "Improve customer satisfaction"],
    callTypes: ["inbound", "outbound"],
    integrations: ["STORIS", "FurnitureDealerNow", "MicroD", "Salesforce"],
    stats: [{ label: "Delivery Efficiency", value: "+30%" }, { label: "Customer Satisfaction", value: "4.7/5" }],
    gradient: "from-amber-600/20 to-yellow-600/20",
    category: "Retail"
  },
  {
    id: "jewelry-stores",
    name: "Jewelry Stores",
    icon: "Sparkles",
    tagline: "Shine with Every Customer Interaction",
    description: "Handle appointment booking, custom order inquiries, and repair status updates with AI agents that add sparkle to your service.",
    useCases: ["Appointment scheduling", "Custom order inquiries", "Repair status updates", "Sizing questions", "Special occasion reminders"],
    benefits: ["Book consultations 24/7", "Handle repair inquiries", "Special occasion marketing", "Improve customer loyalty"],
    callTypes: ["inbound", "outbound"],
    integrations: ["The Edge", "Jewel360", "Salesforce", "Google Calendar"],
    stats: [{ label: "Appointment Rate", value: "+40%" }, { label: "Repeat Customers", value: "+28%" }],
    gradient: "from-yellow-400/20 to-amber-400/20",
    category: "Retail"
  },
  
  // Education & Training
  {
    id: "private-schools",
    name: "Private Schools & Academies",
    icon: "GraduationCap",
    tagline: "Enroll More, Call Back Less",
    description: "Handle admissions inquiries, schedule tours, and manage parent communications with AI agents that represent your institution professionally.",
    useCases: ["Admissions inquiries", "Tour scheduling", "Tuition questions", "Parent communications", "Event reminders"],
    benefits: ["Capture every inquiry", "Schedule tours 24/7", "Improve enrollment rates", "Reduce admin burden"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Blackbaud", "Finalsite", "SchoolAdmin", "Salesforce"],
    stats: [{ label: "Tour Bookings", value: "+55%" }, { label: "Enrollment Rate", value: "+23%" }],
    gradient: "from-indigo-500/20 to-blue-500/20",
    category: "Education"
  },
  {
    id: "tutoring-centers",
    name: "Tutoring & Learning Centers",
    icon: "BookOpen",
    tagline: "Teach More, Admin Less",
    description: "Schedule sessions, handle enrollment inquiries, and manage parent communications with AI agents that help students succeed.",
    useCases: ["Session scheduling", "Enrollment inquiries", "Progress updates", "Payment reminders", "Rescheduling"],
    benefits: ["Book sessions 24/7", "Reduce no-shows", "Improve parent communication", "Handle volume during peak seasons"],
    callTypes: ["inbound", "outbound"],
    integrations: ["TutorCruncher", "Teachworks", "My Music Staff", "Calendly"],
    stats: [{ label: "Enrollment", value: "+35%" }, { label: "No-Show Rate", value: "-45%" }],
    gradient: "from-teal-500/20 to-emerald-500/20",
    category: "Education"
  },
  {
    id: "driving-schools",
    name: "Driving Schools",
    icon: "Car",
    tagline: "Drive Enrollment on Autopilot",
    description: "Schedule lessons, handle permit inquiries, and manage student communications with AI agents that keep your instructors on the road.",
    useCases: ["Lesson scheduling", "Package inquiries", "License/permit questions", "Test scheduling", "Payment processing"],
    benefits: ["Book lessons 24/7", "Handle high volume", "Reduce scheduling conflicts", "Improve student retention"],
    callTypes: ["inbound", "outbound"],
    integrations: ["ScheduleDriving", "BookMeDriving", "DriveScout", "Calendly"],
    stats: [{ label: "Lesson Bookings", value: "+42%" }, { label: "Instructor Utilization", value: "95%" }],
    gradient: "from-green-500/20 to-lime-500/20",
    category: "Education"
  },
  {
    id: "fitness-studios",
    name: "Fitness Studios & Gyms",
    icon: "Dumbbell",
    tagline: "Flex Your Customer Service",
    description: "Handle membership inquiries, class bookings, and personal training scheduling with AI agents that keep your gym running smoothly.",
    useCases: ["Membership inquiries", "Class booking", "Personal training scheduling", "Billing questions", "Cancellation requests"],
    benefits: ["24/7 membership sales", "Reduce front desk burden", "Improve retention", "Handle peak call times"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Mindbody", "ClubReady", "PushPress", "Zen Planner"],
    stats: [{ label: "Membership Sales", value: "+30%" }, { label: "Retention Rate", value: "+22%" }],
    gradient: "from-red-500/20 to-orange-500/20",
    category: "Education"
  },
  {
    id: "music-schools",
    name: "Music Schools & Studios",
    icon: "Music",
    tagline: "Hit the Right Note with Every Call",
    description: "Schedule lessons, handle enrollment inquiries, and manage recital communications with AI agents that orchestrate your operations.",
    useCases: ["Lesson scheduling", "Enrollment inquiries", "Recital reminders", "Instrument rentals", "Teacher availability"],
    benefits: ["Book lessons 24/7", "Handle seasonal demand", "Reduce no-shows", "Improve student retention"],
    callTypes: ["inbound", "outbound"],
    integrations: ["My Music Staff", "Fons", "TutorBird", "Acuity"],
    stats: [{ label: "Enrollment", value: "+38%" }, { label: "Student Retention", value: "92%" }],
    gradient: "from-purple-500/20 to-violet-500/20",
    category: "Education"
  },
  
  // Personal Services
  {
    id: "salons-barbershops",
    name: "Salons & Barbershops",
    icon: "Scissors",
    tagline: "Cut the Missed Calls, Not Just Hair",
    description: "Handle appointment bookings, cancellations, and service inquiries with AI agents that keep your chairs filled.",
    useCases: ["Appointment booking", "Cancellation handling", "Service inquiries", "Stylist availability", "Product recommendations"],
    benefits: ["Book appointments 24/7", "Reduce no-shows by 40%", "Fill last-minute cancellations", "Handle peak call volume"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Vagaro", "Square Appointments", "Booksy", "Boulevard"],
    stats: [{ label: "Chair Utilization", value: "95%" }, { label: "No-Show Reduction", value: "40%" }],
    gradient: "from-pink-500/20 to-rose-500/20",
    category: "Personal Services"
  },
  {
    id: "photography-studios",
    name: "Photography Studios",
    icon: "Camera",
    tagline: "Capture Every Booking Opportunity",
    description: "Schedule shoots, handle inquiries, and manage client communications with AI agents that help you focus on your craft.",
    useCases: ["Session booking", "Package inquiries", "Rescheduling", "Gallery delivery", "Print orders"],
    benefits: ["Book sessions 24/7", "Handle wedding season volume", "Improve client experience", "Reduce admin time"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Honeybook", "Studio Ninja", "17hats", "Calendly"],
    stats: [{ label: "Booking Rate", value: "+45%" }, { label: "Client Satisfaction", value: "4.9/5" }],
    gradient: "from-gray-500/20 to-slate-500/20",
    category: "Personal Services"
  },
  {
    id: "wedding-planners",
    name: "Wedding Planners",
    icon: "Heart",
    tagline: "Say 'I Do' to Every Lead",
    description: "Handle inquiries, schedule consultations, and manage vendor coordination with AI agents that help create perfect weddings.",
    useCases: ["Inquiry handling", "Consultation scheduling", "Vendor coordination", "Timeline reminders", "Payment follow-ups"],
    benefits: ["Capture leads 24/7", "Handle busy season volume", "Improve client communication", "Reduce admin overhead"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Honeybook", "Aisle Planner", "Planning Pod", "Dubsado"],
    stats: [{ label: "Lead Capture", value: "100%" }, { label: "Booking Rate", value: "+52%" }],
    gradient: "from-rose-400/20 to-pink-400/20",
    category: "Personal Services"
  },
  {
    id: "pet-grooming",
    name: "Pet Grooming Services",
    icon: "Dog",
    tagline: "Pamper Pets, Not Phone Lines",
    description: "Schedule grooming appointments, handle breed-specific inquiries, and manage client communications with AI agents that love pets too.",
    useCases: ["Appointment booking", "Service inquiries", "Breed-specific questions", "Pickup reminders", "Recurring appointments"],
    benefits: ["Book appointments 24/7", "Handle peak demand", "Reduce no-shows", "Improve client retention"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Gingr", "PetExec", "Easy Busy Pets", "Acuity"],
    stats: [{ label: "Appointment Rate", value: "+38%" }, { label: "No-Shows", value: "-35%" }],
    gradient: "from-amber-400/20 to-yellow-400/20",
    category: "Personal Services"
  },
  {
    id: "tattoo-studios",
    name: "Tattoo & Piercing Studios",
    icon: "Palette",
    tagline: "Book More Ink, Less Admin",
    description: "Handle consultation requests, scheduling, and aftercare inquiries with AI agents that represent your studio's vibe.",
    useCases: ["Consultation booking", "Artist availability", "Aftercare questions", "Deposit collection", "Rescheduling"],
    benefits: ["Book consultations 24/7", "Reduce no-shows", "Handle high inquiry volume", "Improve artist utilization"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Tattoo Booker", "Fresha", "Square Appointments", "Calendly"],
    stats: [{ label: "Booking Rate", value: "+40%" }, { label: "Artist Utilization", value: "92%" }],
    gradient: "from-gray-600/20 to-neutral-600/20",
    category: "Personal Services"
  },
  
  // Food & Beverage
  {
    id: "catering-services",
    name: "Catering Services",
    icon: "Utensils",
    tagline: "Serve More Events, Stress Less",
    description: "Handle event inquiries, provide quotes, and coordinate logistics with AI agents that help you cater to every client.",
    useCases: ["Event inquiries", "Quote requests", "Menu consultations", "Logistics coordination", "Payment collection"],
    benefits: ["Capture every lead", "Handle busy season", "Improve quote speed", "Reduce planning calls"],
    callTypes: ["inbound", "outbound"],
    integrations: ["CaterTrax", "Total Party Planner", "Foodstorm", "Salesforce"],
    stats: [{ label: "Lead Capture", value: "100%" }, { label: "Quote Speed", value: "10 min" }],
    gradient: "from-orange-500/20 to-amber-500/20",
    category: "Food & Beverage"
  },
  {
    id: "bakeries",
    name: "Bakeries & Cake Shops",
    icon: "Coffee",
    tagline: "Sweet Service, Every Call",
    description: "Handle custom orders, schedule pickups, and manage inquiries with AI agents that keep your bakery running smoothly.",
    useCases: ["Custom order inquiries", "Pickup scheduling", "Menu questions", "Allergen information", "Event orders"],
    benefits: ["Handle order inquiries 24/7", "Reduce missed orders", "Manage holiday rush", "Improve customer service"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Square", "CakeBoss", "BakeIt", "Toast"],
    stats: [{ label: "Order Capture", value: "+35%" }, { label: "Customer Satisfaction", value: "4.8/5" }],
    gradient: "from-pink-400/20 to-rose-400/20",
    category: "Food & Beverage"
  },
  {
    id: "wineries-breweries",
    name: "Wineries & Breweries",
    icon: "Wine",
    tagline: "Pour Perfect Customer Experiences",
    description: "Handle tasting reservations, event bookings, and wine club inquiries with AI agents that share your passion for craft.",
    useCases: ["Tasting reservations", "Event bookings", "Wine/beer club management", "Tour scheduling", "Product inquiries"],
    benefits: ["Book tastings 24/7", "Handle peak season", "Improve club retention", "Capture every inquiry"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Commerce7", "Tock", "VinoShipper", "Square"],
    stats: [{ label: "Tasting Bookings", value: "+42%" }, { label: "Club Retention", value: "+25%" }],
    gradient: "from-purple-600/20 to-violet-600/20",
    category: "Food & Beverage"
  },
  {
    id: "food-trucks",
    name: "Food Trucks & Mobile Vendors",
    icon: "Truck",
    tagline: "Roll with Better Customer Service",
    description: "Handle location inquiries, catering requests, and event bookings with AI agents that keep your mobile business moving.",
    useCases: ["Location inquiries", "Catering requests", "Event bookings", "Menu questions", "Schedule updates"],
    benefits: ["Capture every inquiry", "Handle catering leads", "Reduce missed calls while serving", "Improve event bookings"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Square", "Toast", "FoodTruckEmpire", "Calendly"],
    stats: [{ label: "Catering Leads", value: "+50%" }, { label: "Event Bookings", value: "+35%" }],
    gradient: "from-red-500/20 to-orange-500/20",
    category: "Food & Beverage"
  },
  {
    id: "coffee-shops",
    name: "Coffee Shops & Cafés",
    icon: "Coffee",
    tagline: "Brew Up Better Service",
    description: "Handle catering orders, loyalty inquiries, and large order requests with AI agents that keep your café buzzing.",
    useCases: ["Large orders", "Catering inquiries", "Loyalty program", "Menu questions", "Event bookings"],
    benefits: ["Capture catering leads", "Handle large orders", "Reduce counter interruptions", "Improve loyalty engagement"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Square", "Toast", "Joe Coffee", "Lightspeed"],
    stats: [{ label: "Catering Orders", value: "+40%" }, { label: "Customer Satisfaction", value: "4.7/5" }],
    gradient: "from-amber-700/20 to-yellow-700/20",
    category: "Food & Beverage"
  },
  
  // Technology & IT
  {
    id: "it-services",
    name: "IT Services & MSPs",
    icon: "Monitor",
    tagline: "Support Tickets? We've Got This.",
    description: "Handle support calls, ticket creation, and technical inquiries with AI agents that speak your clients' language.",
    useCases: ["Support ticket creation", "Service inquiries", "Outage notifications", "Onboarding calls", "Contract renewals"],
    benefits: ["24/7 support intake", "Reduce response time", "Improve client satisfaction", "Scale without hiring"],
    callTypes: ["inbound", "outbound"],
    integrations: ["ConnectWise", "Autotask", "Freshdesk", "Zendesk"],
    stats: [{ label: "Ticket Response", value: "<1 min" }, { label: "Client Satisfaction", value: "4.8/5" }],
    gradient: "from-blue-600/20 to-cyan-600/20",
    category: "Technology"
  },
  {
    id: "saas-companies",
    name: "SaaS Companies",
    icon: "Wifi",
    tagline: "Scale Support, Not Headcount",
    description: "Handle trial inquiries, support requests, and renewal calls with AI agents that help your SaaS grow efficiently.",
    useCases: ["Trial inquiries", "Support requests", "Onboarding calls", "Feature questions", "Renewal outreach"],
    benefits: ["Qualify leads 24/7", "Reduce churn with proactive calls", "Handle support volume", "Improve trial conversion"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Salesforce", "HubSpot", "Intercom", "Zendesk"],
    stats: [{ label: "Trial Conversion", value: "+35%" }, { label: "Churn Reduction", value: "18%" }],
    gradient: "from-violet-500/20 to-purple-500/20",
    category: "Technology"
  },
  {
    id: "telecom-providers",
    name: "Telecom & Internet Providers",
    icon: "Radio",
    tagline: "Connect Better with Every Call",
    description: "Handle service inquiries, outage reports, and billing questions with AI agents that reduce call center load.",
    useCases: ["Service inquiries", "Outage reporting", "Billing questions", "Plan changes", "Technical support"],
    benefits: ["Handle high call volume", "Reduce wait times", "Improve customer satisfaction", "24/7 availability"],
    callTypes: ["inbound"],
    integrations: ["CSG", "Amdocs", "Salesforce", "Zendesk"],
    stats: [{ label: "Call Handling", value: "1000+/day" }, { label: "Wait Time", value: "<30 sec" }],
    gradient: "from-cyan-500/20 to-blue-500/20",
    category: "Technology"
  },
  
  // Manufacturing & Industrial
  {
    id: "manufacturing",
    name: "Manufacturing Companies",
    icon: "Factory",
    tagline: "Produce Results, Not Voicemails",
    description: "Handle order inquiries, vendor communications, and customer support with AI agents that keep your production running.",
    useCases: ["Order inquiries", "Delivery status", "Vendor coordination", "Quality concerns", "Quote requests"],
    benefits: ["Handle B2B inquiries 24/7", "Reduce admin overhead", "Improve customer response", "Scale communication"],
    callTypes: ["inbound", "outbound"],
    integrations: ["SAP", "Oracle", "NetSuite", "Salesforce"],
    stats: [{ label: "Inquiry Response", value: "<2 min" }, { label: "Customer Satisfaction", value: "4.6/5" }],
    gradient: "from-gray-600/20 to-zinc-600/20",
    category: "Industrial"
  },
  {
    id: "construction-companies",
    name: "Construction Companies",
    icon: "HardHat",
    tagline: "Build Better Client Relationships",
    description: "Handle bid inquiries, project updates, and subcontractor coordination with AI agents that keep projects on track.",
    useCases: ["Bid inquiries", "Project status updates", "Subcontractor coordination", "Client communications", "Permit status"],
    benefits: ["Capture every bid inquiry", "Improve client communication", "Coordinate subcontractors", "24/7 availability"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Procore", "BuilderTrend", "CoConstruct", "Salesforce"],
    stats: [{ label: "Bid Capture", value: "100%" }, { label: "Client Response", value: "<5 min" }],
    gradient: "from-orange-600/20 to-amber-600/20",
    category: "Industrial"
  },
  {
    id: "logistics-shipping",
    name: "Logistics & Shipping",
    icon: "Package",
    tagline: "Deliver Better Customer Service",
    description: "Handle tracking inquiries, delivery scheduling, and shipping questions with AI agents that keep packages moving.",
    useCases: ["Package tracking", "Delivery scheduling", "Shipping quotes", "Claims processing", "Route optimization"],
    benefits: ["24/7 tracking support", "Reduce call volume", "Improve delivery accuracy", "Handle peak seasons"],
    callTypes: ["inbound"],
    integrations: ["ShipStation", "Shippo", "Easyship", "Salesforce"],
    stats: [{ label: "Tracking Inquiries", value: "500+/day" }, { label: "Resolution Rate", value: "85%" }],
    gradient: "from-blue-700/20 to-indigo-700/20",
    category: "Industrial"
  },
  {
    id: "warehousing",
    name: "Warehousing & Distribution",
    icon: "Warehouse",
    tagline: "Store Up Better Communication",
    description: "Handle inventory inquiries, shipping coordination, and vendor communications with AI agents that optimize your operations.",
    useCases: ["Inventory inquiries", "Shipping coordination", "Vendor communications", "Order status", "Receiving notifications"],
    benefits: ["24/7 inquiry handling", "Improve vendor communication", "Reduce admin overhead", "Better inventory visibility"],
    callTypes: ["inbound", "outbound"],
    integrations: ["SAP", "Oracle WMS", "Manhattan", "Salesforce"],
    stats: [{ label: "Inquiry Response", value: "<3 min" }, { label: "Coordination Efficiency", value: "+40%" }],
    gradient: "from-slate-500/20 to-gray-500/20",
    category: "Industrial"
  },
  
  // Nonprofit & Government
  {
    id: "nonprofits",
    name: "Nonprofit Organizations",
    icon: "Heart",
    tagline: "More Donations, Less Phone Tag",
    description: "Handle donor inquiries, volunteer coordination, and event registrations with AI agents that amplify your impact.",
    useCases: ["Donation inquiries", "Volunteer coordination", "Event registrations", "Program information", "Thank you calls"],
    benefits: ["24/7 donor support", "Handle campaign surges", "Improve donor retention", "Reduce admin costs"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Bloomerang", "Little Green Light", "Salesforce NPSP", "Kindful"],
    stats: [{ label: "Donor Response", value: "100%" }, { label: "Retention Rate", value: "+28%" }],
    gradient: "from-emerald-500/20 to-green-500/20",
    category: "Nonprofit"
  },
  {
    id: "government-services",
    name: "Government Services",
    icon: "Building2",
    tagline: "Serve Citizens, Not Hold Music",
    description: "Handle citizen inquiries, appointment scheduling, and service requests with AI agents that improve public service.",
    useCases: ["Service inquiries", "Appointment scheduling", "Permit status", "Program information", "Complaint intake"],
    benefits: ["24/7 citizen access", "Reduce wait times", "Improve satisfaction", "Handle high volume"],
    callTypes: ["inbound"],
    integrations: ["Accela", "Tyler Technologies", "OpenGov", "Salesforce"],
    stats: [{ label: "Call Handling", value: "1000+/day" }, { label: "Wait Time", value: "-75%" }],
    gradient: "from-blue-800/20 to-indigo-800/20",
    category: "Government"
  },
  {
    id: "religious-organizations",
    name: "Religious Organizations",
    icon: "Heart",
    tagline: "Connect with Your Community",
    description: "Handle event registrations, prayer requests, and member inquiries with AI agents that extend your ministry's reach.",
    useCases: ["Event registrations", "Prayer requests", "Service times", "Volunteer coordination", "Donation inquiries"],
    benefits: ["24/7 community access", "Handle holiday volume", "Improve member engagement", "Coordinate volunteers"],
    callTypes: ["inbound", "outbound"],
    integrations: ["Planning Center", "Breeze", "Church Community Builder", "Salesforce"],
    stats: [{ label: "Community Reach", value: "+45%" }, { label: "Event Registration", value: "+60%" }],
    gradient: "from-purple-700/20 to-indigo-700/20",
    category: "Nonprofit"
  }
];

export const industryCategories = [
  "Healthcare",
  "Real Estate",
  "Hospitality",
  "Automotive",
  "Professional Services",
  "Home Services",
  "Retail",
  "Education",
  "Personal Services",
  "Food & Beverage",
  "Technology",
  "Industrial",
  "Nonprofit",
  "Government"
];

export const getIconComponent = (iconName: string) => {
  const icons: Record<string, any> = {
    Stethoscope, ShoppingBag, Building2, Home, Scale, Utensils, Car, 
    Plane, GraduationCap, Dumbbell, Scissors, Camera, Wrench, Truck,
    CreditCard, Shield, Heart, Baby, Dog, Flower, Music, Gamepad2,
    Palette, BookOpen, Briefcase, Factory, HardHat, Wifi, Zap, 
    Droplets, TreePine, Mountain, Ship, Train, Cog, Microscope,
    Pill, Glasses, Sparkles, Coffee, Wine, Pizza, ShoppingCart,
    Package, Warehouse, Printer, Monitor, Headphones, Radio, Users
  };
  return icons[iconName] || Building2;
};
